import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  Accordion,
  Badge,
  Button,
  Checkbox,
  Combobox,
  Container,
  Group,
  JsonInput,
  Loader,
  MultiSelect,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
  useCombobox,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconLayoutDashboard, IconPlus, IconTrash } from '@tabler/icons-react';

import {
  DataSourceProviders,
  KeyValueEditor,
  LogoPaper,
  objectToPairs,
  PageTitle,
  pairsToObject,
  type ExcludedProviders,
  type KeyValuePair,
} from '@/components';
import { useReportConfigs, useRiskProfiles, useScopeComponents, useTeams } from '@/hooks';
import { vnextApiClient } from '@/services/vnext-api';
import {
  BackofficeTemplate,
  ContentKind,
  Currency,
  CURRENCY_OPTIONS,
  MatchReviewMode,
  PipelineMode,
  ProfileReviewMode,
  ReportBranding,
  ReportConfig,
  ReportConfigComponent,
  ReportConfigComponentBase,
  ScopeComponent,
} from '@/types';
import { apiLeaves, manualLeafSlugs } from '@/utils';

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-|-$/g, '');
}

/**
 * Settings are edited as raw JSON, so the provider checkboxes are a structured
 * editor over one key of that same document rather than a parallel store.
 * `null` means unparseable — the checkboxes go read-only rather than
 * overwriting keys they can't see.
 */
function parseSettings(json: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(json || '{}');
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function readExcludedProviders(settings: Record<string, unknown> | null): ExcludedProviders {
  const raw = settings?.excluded_providers;
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as ExcludedProviders)
    : {};
}

function writeExcludedProviders(
  settings: Record<string, unknown> | null,
  excluded: ExcludedProviders
): string {
  const next = { ...(settings ?? {}) };
  if (Object.keys(excluded).length > 0) {
    next.excluded_providers = excluded;
  } else {
    delete next.excluded_providers;
  }
  return JSON.stringify(next, null, 2);
}

interface ComponentFormValues {
  scopeComponentId: string;
  overridePrompt: string;
  settings: string; // JSON string
  // Display-only fields from the resolved scope component
  name: string;
  slug: string;
  description: string;
  hasApiDependency: boolean;
}

interface FormValues {
  name: string;
  slug: string;
  description: string;
  /** Programme context threaded into the screening prompts (admin prose). */
  screeningContext: string;
  /** Teams this config is applicable to; EMPTY = every team. */
  teamIds: string[];
  isActive: boolean;
  /** Client visibility: offer this config in the pickers of every
   * applicable team's clients. Off = admin-only. */
  visible: boolean;
  approvalEnabled: boolean;
  turnaroundTimeDays: number | '';
  price: number | '';
  currency: Currency;
  triageRequired: boolean;
  requiresClientActivation: boolean;
  /** Manual / Backoffice-first (docx) fulfilment flow. When on, the
   * scope-component picker is replaced by a Backoffice Template selector and
   * triage / approval / client-activation default on (editable, not locked). */
  legacyFlow: boolean;
  backofficeTemplateId: number | null;
  backofficeTemplateName: string | null;
  /** Report output pipeline (plan 15): markdown assembly or docx template fill. */
  contentKind: ContentKind;
  /** Cover, header and styling of the downloaded PDF report; 'gti' is the
   * default and is sent as null. */
  branding: ReportBranding;
  /** Pipeline depth: full, or basic-screening (identification + deep
   * research skipped; required by screening batches). */
  pipelineMode: PipelineMode;
  /** Analyst gate between remediation and synthesis, and the match
   * probability below which a retained match is queued for review. */
  matchReview: MatchReviewMode;
  matchReviewThreshold: number;
  /** Who confirms the social media profiles before the screening is ordered. */
  profileReview: ProfileReviewMode;
  /** Rating policy (risk profile id); '' = the platform default. */
  riskProfileId: string;
  /** Mandated named-style set for docx narrative rendering (e.g. gti-v1). */
  styleContract: string;
  /** Default report-level fill variables, edited as key/value rows. */
  reportMetadataDefaults: KeyValuePair[];
  components: ComponentFormValues[];
}

function componentToFormValues(component: ReportConfigComponent): ComponentFormValues {
  return {
    scopeComponentId: component.scopeComponentId,
    overridePrompt: component.overridePrompt ?? '',
    settings: component.settings ? JSON.stringify(component.settings, null, 2) : '{}',
    name: component.scopeComponent?.name ?? component.scopeComponentId,
    slug: component.scopeComponent?.slug ?? '',
    description: component.scopeComponent?.description ?? '',
    hasApiDependency: component.scopeComponent?.hasApiDependency ?? false,
  };
}

function scopeComponentToFormValues(sc: ScopeComponent): ComponentFormValues {
  return {
    scopeComponentId: sc.id,
    overridePrompt: '',
    settings: '{}',
    name: sc.name,
    slug: sc.slug,
    description: sc.description ?? '',
    hasApiDependency: sc.hasApiDependency ?? false,
  };
}

function formValuesToComponentBase(values: ComponentFormValues): ReportConfigComponentBase {
  return {
    scope_component_id: values.scopeComponentId,
    override_prompt: values.overridePrompt || null,
    settings: JSON.parse(values.settings || '{}'),
  };
}

function configToFormValues(config: ReportConfig): FormValues {
  return {
    name: config.name,
    slug: config.slug,
    description: config.description ?? '',
    screeningContext: config.screeningContext ?? '',
    teamIds: config.teamIds,
    isActive: config.isActive,
    visible: config.visible,
    approvalEnabled: config.approvalRequired === 'enabled',
    turnaroundTimeDays: config.turnaroundTimeDays ?? '',
    price: config.price ?? '',
    currency: config.currency,
    triageRequired: config.triageRequired,
    requiresClientActivation: config.requiresClientActivation,
    legacyFlow: config.legacyFlow,
    backofficeTemplateId: config.backofficeTemplateId,
    backofficeTemplateName: config.backofficeTemplateName,
    contentKind: config.contentKind,
    branding: config.branding ?? 'gti',
    pipelineMode: config.pipelineMode,
    matchReview: config.matchReview,
    matchReviewThreshold: config.matchReviewThreshold,
    profileReview: config.profileReview,
    riskProfileId: config.riskProfileId ?? '',
    styleContract: config.styleContract ?? '',
    reportMetadataDefaults: objectToPairs(config.reportMetadataDefaults),
    components: config.components.map(componentToFormValues),
  };
}

function getInitialFormValues(): FormValues {
  return {
    name: '',
    slug: '',
    description: '',
    screeningContext: '',
    teamIds: [],
    isActive: true,
    visible: true,
    approvalEnabled: false,
    turnaroundTimeDays: '',
    price: '',
    currency: 'USD',
    triageRequired: false,
    requiresClientActivation: false,
    legacyFlow: false,
    backofficeTemplateId: null,
    backofficeTemplateName: null,
    contentKind: 'markdown',
    branding: 'gti',
    pipelineMode: 'full',
    matchReview: 'off',
    matchReviewThreshold: 0.9,
    profileReview: 'off',
    riskProfileId: '',
    styleContract: '',
    reportMetadataDefaults: [],
    components: [],
  };
}

function numberOrNull(value: number | ''): number | null {
  return value === '' ? null : value;
}

/** The GTI branding is the default, stored as null on the config. */
function brandingOrNull(value: ReportBranding): ReportBranding | null {
  return value === 'gti' ? null : value;
}

const BRANDING_OPTIONS: { value: ReportBranding; label: string }[] = [
  { value: 'gti', label: 'GTI (default)' },
  { value: 'perrett-laver', label: 'Perrett Laver (co-branded)' },
];

/** Branding styles only the PDF rendered from a markdown report: docx-content
 * and legacy-flow configs convert their own docx. */
const BRANDING_DESCRIPTION =
  'Cover, header and styling of the downloaded PDF report (markdown reports only)';

interface ModeSelectProps<T extends string> {
  label: string;
  description: string;
  data: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  testId?: string;
  disabled?: boolean;
}

/** A closed-vocabulary config setting: Mantine's Select with deselection
 * off and the value typed to the enum. */
function ModeSelect<T extends string>({
  label,
  description,
  data,
  value,
  onChange,
  testId,
  disabled,
}: Readonly<ModeSelectProps<T>>) {
  return (
    <Select
      label={label}
      description={description}
      data={data}
      value={value}
      onChange={(next) => {
        if (next) {
          onChange(next as T);
        }
      }}
      allowDeselect={false}
      disabled={disabled}
      data-testid={testId}
    />
  );
}

interface AddComponentComboboxProps {
  /** Scope components not yet added to the config. */
  components: ScopeComponent[];
  onAdd: (sc: ScopeComponent) => void;
}

/**
 * Searchable "Add Component" picker.
 *
 * The scope-component catalogue runs to hundreds of entries, so the previous
 * flat Menu of every option overflowed the viewport. This renders a compact
 * combobox: a search field plus a fixed-height scroll area, filtered by name
 * and description as you type.
 */
function AddComponentCombobox({ components, onAdd }: Readonly<AddComponentComboboxProps>) {
  const [search, setSearch] = useState('');
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
    onDropdownOpen: () => combobox.focusSearchInput(),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return components;
    }
    return components.filter(
      (sc) =>
        sc.name.toLowerCase().includes(q) || (sc.description?.toLowerCase().includes(q) ?? false)
    );
  }, [components, search]);

  const handleOptionSubmit = (id: string) => {
    const sc = components.find((c) => c.id === id);
    if (sc) {
      onAdd(sc);
    }
    // Keep the dropdown open so several components can be added in a row;
    // the chosen one drops out of `components` on the next render.
    setSearch('');
  };

  return (
    <Combobox
      store={combobox}
      width={320}
      position="bottom-end"
      withinPortal
      onOptionSubmit={handleOptionSubmit}
    >
      <Combobox.Target>
        <Button
          leftSection={<IconPlus size={16} />}
          variant="light"
          size="sm"
          onClick={() => combobox.toggleDropdown()}
        >
          Add Component
        </Button>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          placeholder="Search components…"
        />
        <Combobox.Options>
          <ScrollArea.Autosize mah={260} type="scroll">
            {filtered.length === 0 ? (
              <Combobox.Empty>No matching components</Combobox.Empty>
            ) : (
              filtered.map((sc) => (
                <Combobox.Option value={sc.id} key={sc.id}>
                  <Stack gap={2}>
                    <Group gap={6} wrap="nowrap">
                      <Text size="sm" fw={500}>
                        {sc.name}
                      </Text>
                      {sc.componentKind === 'data_source' && (
                        <Badge size="xs" variant="light" color="grape">
                          data
                        </Badge>
                      )}
                    </Group>
                    {sc.description && (
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {sc.description}
                      </Text>
                    )}
                  </Stack>
                </Combobox.Option>
              ))
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

export function ReportConfigEditPage() {
  const { configId } = useParams<{ configId: string }>();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('team_id') ?? undefined;
  const navigate = useNavigate();
  const isNew = !configId;

  const { getConfig, createConfig, updateConfig, setComponents } = useReportConfigs({
    teamId,
    autoFetch: false,
  });
  const { scopeComponents } = useScopeComponents({ includeAll: true });
  const { teams, isLoading: teamsLoading } = useTeams();
  const riskProfiles = useRiskProfiles();
  const teamSelectData = teams.map((t) => ({ value: String(t.id), label: t.name }));

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  // Tracks approval_required state as it was when the config was loaded, so
  // we can detect a transition from disabled -> enabled and offer retroactive
  // bulk approval.
  const [initialApprovalEnabled, setInitialApprovalEnabled] = useState(false);
  const [retroactivelyApprove, setRetroactivelyApprove] = useState(false);

  const form = useForm<FormValues>({
    mode: 'controlled',
    // Seed the team from the `?team_id` the author arrived with (carried over
    // from the list page's team filter); they can still change it before
    // creating. On edit this is overwritten by the loaded config's team.
    initialValues: { ...getInitialFormValues(), teamIds: teamId ? [teamId] : [] },
    validate: {
      name: (value) => (!value.trim() ? 'Name is required' : null),
      slug: (value) => (!value.trim() ? 'Slug is required' : null),
    },
  });

  const loadConfig = useCallback(async () => {
    if (!configId) {
      return;
    }
    try {
      setLoading(true);
      const config = await getConfig(configId);
      form.setValues(configToFormValues(config));
      setSlugManuallyEdited(true);
      setInitialApprovalEnabled(config.approvalRequired === 'enabled');
    } catch {
      // Error handled by hook
    } finally {
      setLoading(false);
    }
  }, [configId, getConfig]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleNameChange = (value: string) => {
    form.setFieldValue('name', value);
    if (!slugManuallyEdited) {
      form.setFieldValue('slug', slugify(value));
    }
  };

  const addedComponentIds = useMemo(
    () => new Set(form.values.components.map((c) => c.scopeComponentId)),
    [form.values.components]
  );

  // Sections (components with dependencies) are addable, plus data-source
  // components: attaching one puts its output schema's values into the docx
  // template-mapping catalog. Other dependency-less leaves stay hidden.
  const availableComponents = useMemo(
    () =>
      scopeComponents.filter(
        (sc) =>
          !addedComponentIds.has(sc.id) &&
          (sc.dependencies.length > 0 || sc.componentKind === 'data_source')
      ),
    [scopeComponents, addedComponentIds]
  );

  const componentsBySlug = useMemo(
    () => new Map(scopeComponents.map((c) => [c.slug, c])),
    [scopeComponents]
  );

  // Which sections use each data source. Exclusions are per-section but only
  // take effect where every section using the source agrees, so the provider
  // controls need to name the siblings that would override a change.
  const sectionsByLeafSlug = useMemo(() => {
    const map = new Map<string, Array<{ id: string; name: string }>>();
    for (const component of form.values.components) {
      const sc = componentsBySlug.get(component.slug);
      if (!sc) {
        continue;
      }
      for (const leaf of apiLeaves(sc, componentsBySlug)) {
        const users = map.get(leaf.slug) ?? [];
        users.push({ id: component.scopeComponentId, name: component.name });
        map.set(leaf.slug, users);
      }
    }
    return map;
  }, [form.values.components, componentsBySlug]);

  // Backoffice templates for the legacy-flow selector — fetched lazily the
  // first time legacy_flow is switched on (or when editing a legacy config).
  const [backofficeTemplates, setBackofficeTemplates] = useState<BackofficeTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  // Guards the one-shot fetch. Keyed by team so switching team re-fetches.
  // NOT via effect deps: `templatesLoading` / `backofficeTemplates.length` are
  // set BY this effect, so listing them as deps re-fires it mid-flight (the
  // cleanup cancels the in-flight fetch, and the result is silently dropped —
  // leaving the dropdown stuck on "Loading templates…").
  const templatesFetchedForTeam = useRef<string | null>(null);
  const legacyFlow = form.values.legacyFlow;

  useEffect(() => {
    if (!legacyFlow) {
      return;
    }
    const key = teamId ?? '';
    if (templatesFetchedForTeam.current === key) {
      return;
    }
    templatesFetchedForTeam.current = key;

    let cancelled = false;
    setTemplatesLoading(true);
    setTemplatesError(null);
    vnextApiClient
      .getBackofficeTemplates(teamId)
      .then((templates) => {
        if (!cancelled) {
          setBackofficeTemplates(templates);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTemplatesError('Failed to load Backoffice templates');
          // Allow a retry (e.g. re-toggle / team change) after a failure.
          templatesFetchedForTeam.current = null;
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTemplatesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [legacyFlow, teamId]);

  const selectedTemplate = useMemo(
    () => backofficeTemplates.find((t) => t.id === form.values.backofficeTemplateId) ?? null,
    [backofficeTemplates, form.values.backofficeTemplateId]
  );

  const handleAddComponent = (sc: ScopeComponent) => {
    form.insertListItem('components', scopeComponentToFormValues(sc));
  };

  const handleRemoveComponent = (index: number) => {
    form.removeListItem('components', index);
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setSaving(true);

      // Legacy configs are template-driven (no Hub scope components) — persist
      // an empty component list. Triage / approval / client-activation are the
      // author's choice (pre-selected on when legacy is toggled, but editable),
      // so send the actual form values rather than coercing.
      const componentData = values.legacyFlow
        ? []
        : values.components.map(formValuesToComponentBase);
      const approvalRequired = values.approvalEnabled ? 'enabled' : 'disabled';

      if (isNew || !configId) {
        const created = await createConfig({
          name: values.name,
          slug: values.slug,
          description: values.description || undefined,
          screening_context: values.screeningContext.trim() || undefined,
          is_active: values.isActive,
          approval_required: approvalRequired,
          turnaround_time_days: numberOrNull(values.turnaroundTimeDays),
          price: numberOrNull(values.price),
          currency: values.currency,
          triage_required: values.triageRequired,
          requires_client_activation: values.requiresClientActivation,
          legacy_flow: values.legacyFlow,
          backoffice_template_id: values.legacyFlow ? values.backofficeTemplateId : null,
          backoffice_template_name: values.legacyFlow ? values.backofficeTemplateName : null,
          content_kind: values.contentKind,
          branding: brandingOrNull(values.branding),
          pipeline_mode: values.pipelineMode,
          match_review: values.matchReview,
          match_review_threshold: values.matchReviewThreshold,
          profile_review: values.profileReview,
          risk_profile_id: values.riskProfileId || null,
          style_contract: values.styleContract || null,
          report_metadata_defaults: pairsToObject(values.reportMetadataDefaults),
          team_ids: values.teamIds,
          visible: values.visible,
          components: componentData,
        });
        // After creating a config, drop the author into the template editor
        // for the new config so they can customise the template right away.
        // Legacy-flow configs skip it: their fulfilment is driven by the
        // linked Backoffice docx template, not template blocks — go back to
        // the list instead. Carry the chosen team forward either way.
        const params = values.teamIds[0] ? `?team_id=${values.teamIds[0]}` : '';
        if (values.legacyFlow) {
          navigate(`/admin/report-configs${params}`);
        } else {
          navigate(`/admin/report-configs/${created.id}/template${params}`);
        }
        return;
      }
      // Update metadata first
      await updateConfig(configId, {
        name: values.name,
        slug: values.slug,
        description: values.description || null,
        screening_context: values.screeningContext.trim() || null,
        is_active: values.isActive,
        visible: values.visible,
        team_ids: values.teamIds,
        approval_required: approvalRequired,
        turnaround_time_days: numberOrNull(values.turnaroundTimeDays),
        price: numberOrNull(values.price),
        currency: values.currency,
        triage_required: values.triageRequired,
        requires_client_activation: values.requiresClientActivation,
        legacy_flow: values.legacyFlow,
        backoffice_template_id: values.legacyFlow ? values.backofficeTemplateId : null,
        backoffice_template_name: values.legacyFlow ? values.backofficeTemplateName : null,
        content_kind: values.contentKind,
        branding: brandingOrNull(values.branding),
        pipeline_mode: values.pipelineMode,
        match_review: values.matchReview,
        match_review_threshold: values.matchReviewThreshold,
        profile_review: values.profileReview,
        risk_profile_id: values.riskProfileId || null,
        style_contract: values.styleContract || null,
        report_metadata_defaults: pairsToObject(values.reportMetadataDefaults),
      });

      // Then set components separately
      await setComponents(configId, componentData);

      // If approval was just enabled AND admin asked to retroactively approve,
      // call the bulk-approve endpoint so existing completed reports remain
      // visible to clients.
      const approvalWasJustEnabled = !initialApprovalEnabled && values.approvalEnabled;
      if (approvalWasJustEnabled && retroactivelyApprove) {
        try {
          const result = await vnextApiClient.bulkApproveReportsForConfig(configId, teamId);
          notifications.show({
            title: 'Existing reports approved',
            message: `${result.approved} existing report(s) were auto-approved.`,
            color: 'green',
          });
        } catch (err) {
          notifications.show({
            title: 'Bulk approval failed',
            message:
              err instanceof Error ? err.message : 'Existing reports could not be auto-approved.',
            color: 'red',
          });
        }
      }

      const params = teamId ? `?team_id=${teamId}` : '';
      navigate(`/admin/report-configs${params}`);
    } catch {
      // Error handled by hook notifications
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const params = teamId ? `?team_id=${teamId}` : '';
    navigate(`/admin/report-configs${params}`);
  };

  if (loading) {
    return (
      <Stack gap="xl">
        <PageTitle
          title={isNew ? 'Create Configuration' : 'Edit Configuration'}
          subtitle="Loading..."
        />
        <Container size="md" px={0}>
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        </Container>
      </Stack>
    );
  }

  return (
    <Stack gap="xl">
      <Group justify="space-between" align="flex-start">
        <PageTitle
          title={isNew ? 'Create Configuration' : 'Edit Configuration'}
          subtitle={isNew ? 'Set up a new report configuration' : `Editing: ${form.values.name}`}
        />
        {!isNew && configId && (
          <Button
            variant="default"
            leftSection={<IconLayoutDashboard size={16} />}
            onClick={() => {
              const params = teamId ? `?team_id=${teamId}` : '';
              navigate(`/admin/report-configs/${configId}/template${params}`);
            }}
          >
            Edit template
          </Button>
        )}
      </Group>

      <Container size="md" px={0}>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="lg">
            <LogoPaper>
              <Stack gap="md">
                <Title order={4}>General</Title>

                <MultiSelect
                  label="Teams"
                  description="Teams this configuration is offered to. Leave empty to make it applicable to every team."
                  placeholder={form.values.teamIds.length === 0 ? 'All teams' : undefined}
                  data={teamSelectData}
                  value={form.values.teamIds}
                  onChange={(value) => form.setFieldValue('teamIds', value)}
                  clearable
                  searchable
                  disabled={teamsLoading}
                  data-testid="config-teams"
                />

                <TextInput
                  withAsterisk
                  label="Name"
                  placeholder="e.g. Acme Corp Standard"
                  {...form.getInputProps('name')}
                  onChange={(e) => handleNameChange(e.currentTarget.value)}
                />

                <TextInput
                  withAsterisk
                  label="Slug"
                  placeholder="e.g. acme-corp-standard"
                  description="URL-safe identifier. Auto-generated from name."
                  {...form.getInputProps('slug')}
                  onChange={(e) => {
                    setSlugManuallyEdited(true);
                    form.setFieldValue('slug', e.currentTarget.value);
                  }}
                />

                <TextInput
                  label="Description"
                  placeholder="Describe this configuration"
                  {...form.getInputProps('description')}
                />

                <Textarea
                  label="Screening context"
                  placeholder="Who the client is and what their counterparties look like — e.g. 'Tobacco manufacturer screening third-party suppliers, predominantly Benelux SMEs.'"
                  description="Threaded into the screening match and severity prompts. Informs how much corroboration a match needs; never overrides identity evidence."
                  autosize
                  minRows={2}
                  {...form.getInputProps('screeningContext')}
                />

                <Group grow align="flex-start">
                  <NumberInput
                    label="Turnaround time (business days)"
                    placeholder="e.g. 5"
                    description="Default TAT. Reports can override per-report."
                    min={0}
                    allowDecimal={false}
                    {...form.getInputProps('turnaroundTimeDays')}
                  />
                  <NumberInput
                    label="Price"
                    placeholder="e.g. 1200.00"
                    description="Default price. Reports can override per-report."
                    min={0}
                    decimalScale={2}
                    fixedDecimalScale
                    {...form.getInputProps('price')}
                  />
                  <Select
                    label="Currency"
                    data={CURRENCY_OPTIONS.map((c) => ({ value: c, label: c }))}
                    value={form.values.currency}
                    onChange={(value) => {
                      if (value) {
                        form.setFieldValue('currency', value as Currency);
                      }
                    }}
                    allowDeselect={false}
                  />
                </Group>

                <Group>
                  <Switch
                    label="Active"
                    description="Enable this configuration for use"
                    {...form.getInputProps('isActive', { type: 'checkbox' })}
                  />
                </Group>

                <Switch
                  label="Visible to clients"
                  description="Offer this configuration in the config pickers of its applicable teams' clients. Off keeps it admin-only (admin management and existing reports are unaffected)."
                  {...form.getInputProps('visible', { type: 'checkbox' })}
                />

                <Stack gap="xs">
                  <Switch
                    label="Require approval"
                    description="Reports under this config must be approved by an admin before clients can view them"
                    {...form.getInputProps('approvalEnabled', { type: 'checkbox' })}
                  />
                  {!isNew && !initialApprovalEnabled && form.values.approvalEnabled && (
                    <Checkbox
                      label="Auto-approve all existing completed reports"
                      description="Creates approval records for reports already generated under this config so clients don't lose visibility."
                      checked={retroactivelyApprove}
                      onChange={(e) => setRetroactivelyApprove(e.currentTarget.checked)}
                      ml="xl"
                    />
                  )}

                  <Switch
                    label="Require analyst triage"
                    description="Requests for this config land in the triage queue for analyst review instead of creating a Report directly."
                    {...form.getInputProps('triageRequired', { type: 'checkbox' })}
                  />

                  <Switch
                    label="Require client activation"
                    description={
                      form.values.triageRequired
                        ? 'After triage, the report waits for the client (or an admin) to press Start before it runs.'
                        : 'Only applies when analyst triage is required — enable that first.'
                    }
                    disabled={!form.values.triageRequired}
                    {...form.getInputProps('requiresClientActivation', { type: 'checkbox' })}
                  />

                  <Switch
                    label="Legacy (Backoffice docx) flow"
                    description="Fulfil reports manually via a Network Partner against a Django Backoffice Template (a human-authored .docx) instead of the automated scope-component pipeline. Enables triage, approval, and client activation by default (all editable)."
                    checked={form.values.legacyFlow}
                    onChange={(e) => {
                      const on = e.currentTarget.checked;
                      form.setFieldValue('legacyFlow', on);
                      // Turning legacy on pre-selects the flow's usual gates as
                      // sensible defaults — but they stay editable (not locked).
                      if (on) {
                        form.setFieldValue('triageRequired', true);
                        form.setFieldValue('approvalEnabled', true);
                        form.setFieldValue('requiresClientActivation', true);
                      }
                    }}
                  />
                </Stack>

                <ModeSelect<PipelineMode>
                  label="Pipeline depth"
                  description="Full runs subject identification + deep research. Basic screening trusts the submitted subject details as the identity anchor and skips both — the low-cost screening tier, required for screening batches. Snapshotted onto each report at creation."
                  data={[
                    { value: 'full', label: 'Full pipeline' },
                    {
                      value: 'screening_only',
                      label: 'Basic screening (skip identification & research)',
                    },
                  ]}
                  value={form.values.pipelineMode}
                  onChange={(value) => form.setFieldValue('pipelineMode', value)}
                />

                <Stack gap="xs">
                  <ModeSelect<MatchReviewMode>
                    label="Match review"
                    description="Have an analyst adjudicate remediation matches before synthesis. Low confidence queues retained matches below the threshold, uncertain non-matches and cross-validation demotions; All queues every retained match. Switching it off releases any report parked at the gate."
                    data={[
                      { value: 'off', label: 'Off' },
                      { value: 'low_confidence', label: 'Low-confidence matches' },
                      { value: 'all', label: 'All retained matches' },
                    ]}
                    value={form.values.matchReview}
                    onChange={(value) => form.setFieldValue('matchReview', value)}
                    testId="match-review-mode"
                  />
                  <NumberInput
                    label="Match review threshold"
                    description={
                      form.values.matchReview === 'low_confidence'
                        ? 'Retained matches whose probability of being the subject is below this are queued for review. Model confidences cluster at 0.98 / 0.95 / 0.92 / 0.85 — 0.9 is the first cut that separates confident from hedged.'
                        : 'Only applies in low-confidence mode.'
                    }
                    min={0.5}
                    max={1}
                    step={0.05}
                    decimalScale={2}
                    disabled={form.values.matchReview !== 'low_confidence'}
                    value={form.values.matchReviewThreshold}
                    onChange={(value) => {
                      if (typeof value === 'number') {
                        form.setFieldValue('matchReviewThreshold', value);
                      }
                    }}
                    data-testid="match-review-threshold"
                  />
                </Stack>

                <ModeSelect<ProfileReviewMode>
                  label="Profile review"
                  description="Who confirms the social media profiles before the screening is ordered: the requesting team or an admin. 'Only unconfirmed' pauses only when the system could not vouch for a profile."
                  data={[
                    { value: 'off', label: 'Off' },
                    { value: 'unconfirmed', label: 'Only unconfirmed profiles' },
                    { value: 'all', label: 'Every profile' },
                  ]}
                  value={form.values.profileReview}
                  onChange={(value) => form.setFieldValue('profileReview', value)}
                  testId="profile-review-mode"
                />

                <Select
                  label="Risk profile"
                  description="The rating policy reports under this config are judged by: which retained findings count as a hit, which as potential. Leave unset for the platform default. Changing it re-rates this config's reports from their findings."
                  placeholder={
                    riskProfiles.platformDefault
                      ? `Platform default (${riskProfiles.platformDefault.name})`
                      : 'Platform default'
                  }
                  data={riskProfiles.profiles
                    .filter((p) => !p.isPlatformDefault)
                    .map((p) => ({ value: p.id, label: p.name }))}
                  value={form.values.riskProfileId || null}
                  onChange={(value) => form.setFieldValue('riskProfileId', value ?? '')}
                  clearable
                  searchable
                  data-testid="risk-profile-select"
                />

                <Stack gap="xs">
                  <Group grow align="flex-start">
                    <ModeSelect<ContentKind>
                      label="Report output"
                      description="Markdown assembly (default) or docx template fill (plan 15). Snapshotted onto each report at creation."
                      data={[
                        { value: 'markdown', label: 'Markdown (assembled + composed)' },
                        { value: 'docx', label: 'Docx (authored-template fill)' },
                      ]}
                      value={form.values.contentKind}
                      onChange={(value) => form.setFieldValue('contentKind', value)}
                    />
                    <ModeSelect<ReportBranding>
                      label="PDF branding"
                      description={BRANDING_DESCRIPTION}
                      data={BRANDING_OPTIONS}
                      value={form.values.branding}
                      onChange={(value) => form.setFieldValue('branding', value)}
                      testId="pdf-branding"
                      // Docx-content configs convert their own filled template
                      // to PDF, so the branding has no effect on them.
                      disabled={form.values.contentKind === 'docx'}
                    />
                  </Group>
                  {form.values.contentKind === 'docx' && (
                    <>
                      <TextInput
                        label="Style contract"
                        placeholder="gti-v1"
                        description="Mandated named-style set the template must define; narrative renders into these styles."
                        {...form.getInputProps('styleContract')}
                      />
                      <KeyValueEditor
                        label="Report metadata defaults"
                        description="Report-level fill variables (matter reference, report date, …). Keys become bindable report_metadata.* catalog entries; reports override values per-report."
                        pairs={form.values.reportMetadataDefaults}
                        onChange={(pairs) => form.setFieldValue('reportMetadataDefaults', pairs)}
                        keyPlaceholder="matter_ref"
                        valuePlaceholder="default value (may be empty)"
                        addLabel="Add metadata key"
                      />
                      {!isNew && configId && (
                        <Group>
                          <Button
                            variant="light"
                            leftSection={<IconLayoutDashboard size={16} />}
                            onClick={() =>
                              navigate(`/admin/report-configs/${configId}/docx-mapping`)
                            }
                          >
                            Open template mapping
                          </Button>
                          <Text size="xs" c="dimmed">
                            Upload the styled .docx and bind its regions to platform data.
                          </Text>
                        </Group>
                      )}
                    </>
                  )}
                </Stack>
              </Stack>
            </LogoPaper>

            {form.values.legacyFlow ? (
              <LogoPaper>
                <Stack gap="md">
                  <div>
                    <Title order={4}>Backoffice Template</Title>
                    <Text size="sm" c="dimmed">
                      Link this legacy-flow config to a Django Backoffice Template. The
                      template&apos;s scope components (fetched live) drive the NP assignments built
                      at triage.
                    </Text>
                  </div>

                  {templatesError && (
                    <Text size="sm" c="red">
                      {templatesError}
                    </Text>
                  )}

                  <Select
                    label="Template"
                    placeholder={templatesLoading ? 'Loading templates…' : 'Select a template'}
                    disabled={templatesLoading}
                    searchable
                    data={backofficeTemplates.map((t) => ({
                      value: String(t.id),
                      label: t.name,
                    }))}
                    value={
                      form.values.backofficeTemplateId != null
                        ? String(form.values.backofficeTemplateId)
                        : null
                    }
                    onChange={(value) => {
                      const id = value ? Number(value) : null;
                      const template = backofficeTemplates.find((t) => t.id === id) ?? null;
                      form.setFieldValue('backofficeTemplateId', id);
                      form.setFieldValue('backofficeTemplateName', template?.name ?? null);
                    }}
                  />

                  {selectedTemplate && (
                    <Stack gap="xs">
                      <Text size="sm" fw={500}>
                        Template components ({selectedTemplate.components.length})
                      </Text>
                      {selectedTemplate.components.length === 0 ? (
                        <Text size="sm" c="dimmed">
                          This template has no scope components.
                        </Text>
                      ) : (
                        <Group gap="xs">
                          {selectedTemplate.components.map((c) => (
                            <Badge
                              key={`${c.slug ?? c.name}`}
                              variant="light"
                              color={c.isAutomated ? 'blue' : 'gray'}
                            >
                              {c.name}
                            </Badge>
                          ))}
                        </Group>
                      )}
                    </Stack>
                  )}
                </Stack>
              </LogoPaper>
            ) : (
              <LogoPaper>
                <Stack gap="md">
                  <Group justify="space-between">
                    <div>
                      <Title order={4}>Components</Title>
                      <Text size="sm" c="dimmed">
                        Configure which scope components are enabled and their settings.
                      </Text>
                    </div>
                    {availableComponents.length > 0 && (
                      <AddComponentCombobox
                        components={availableComponents}
                        onAdd={handleAddComponent}
                      />
                    )}
                  </Group>

                  {form.values.components.length === 0 ? (
                    <Text c="dimmed" ta="center" py="md">
                      No components added yet. Use the button above to add scope components.
                    </Text>
                  ) : (
                    <Accordion variant="separated">
                      {form.values.components.map((component, index) => (
                        <Accordion.Item
                          key={component.scopeComponentId}
                          value={component.scopeComponentId}
                        >
                          <Accordion.Control>
                            <Group justify="space-between" pr="md" wrap="nowrap">
                              <Stack gap={2}>
                                <Text fw={500}>{component.name}</Text>
                                {component.description && (
                                  <Text size="xs" c="dimmed">
                                    {component.description}
                                  </Text>
                                )}
                              </Stack>
                              <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                                {component.hasApiDependency && (
                                  <Badge
                                    size="sm"
                                    variant="light"
                                    color="blue"
                                    title="This section uses one or more external API data sources"
                                  >
                                    API
                                  </Badge>
                                )}
                                {(() => {
                                  const sc = componentsBySlug.get(component.slug);
                                  const manual = sc ? manualLeafSlugs(sc, componentsBySlug) : [];
                                  return manual.length > 0 ? (
                                    <Badge
                                      size="sm"
                                      variant="light"
                                      color="orange"
                                      title={`Includes manual (human) steps: ${manual.join(', ')}`}
                                    >
                                      Manual
                                    </Badge>
                                  ) : null;
                                })()}
                              </Group>
                            </Group>
                          </Accordion.Control>
                          <Accordion.Panel>
                            <Stack gap="sm">
                              <Textarea
                                label="Override Prompt"
                                placeholder="Leave empty to use the default prompt"
                                minRows={3}
                                autosize
                                {...form.getInputProps(`components.${index}.overridePrompt`)}
                              />

                              {(() => {
                                const sc = componentsBySlug.get(component.slug);
                                const leaves = sc ? apiLeaves(sc, componentsBySlug) : [];
                                const parsed = parseSettings(component.settings);
                                const sharedWith = Object.fromEntries(
                                  leaves.map((leaf) => [
                                    leaf.slug,
                                    (sectionsByLeafSlug.get(leaf.slug) ?? [])
                                      .filter((s) => s.id !== component.scopeComponentId)
                                      .map((s) => s.name),
                                  ])
                                );
                                return (
                                  <DataSourceProviders
                                    leaves={leaves}
                                    excluded={readExcludedProviders(parsed)}
                                    disabled={parsed === null}
                                    sharedWith={sharedWith}
                                    onChange={(next) =>
                                      form.setFieldValue(
                                        `components.${index}.settings`,
                                        writeExcludedProviders(parsed, next)
                                      )
                                    }
                                  />
                                );
                              })()}

                              <JsonInput
                                label="Settings"
                                placeholder="{}"
                                minRows={3}
                                maxRows={8}
                                autosize
                                formatOnBlur
                                validationError="Invalid JSON"
                                {...form.getInputProps(`components.${index}.settings`)}
                              />

                              <Group justify="flex-end">
                                <Button
                                  variant="subtle"
                                  color="red"
                                  size="xs"
                                  leftSection={<IconTrash size={14} />}
                                  onClick={() => handleRemoveComponent(index)}
                                >
                                  Remove
                                </Button>
                              </Group>
                            </Stack>
                          </Accordion.Panel>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  )}
                </Stack>
              </LogoPaper>
            )}

            <Group justify="flex-end">
              <Button variant="default" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" loading={saving}>
                {isNew ? 'Create Configuration' : 'Save Changes'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Container>
    </Stack>
  );
}
